import { motion } from "framer-motion";
import { BrainCircuit } from "lucide-react";

const TEAM = [
  {
    initials: "AR",
    name: "Aarav Raman",
    role: "Head of AI Engineering",
    exp: "12 years · Large language models, agent orchestration, MLOps",
  },
  {
    initials: "SM",
    name: "Sarah Mitchell",
    role: "Lead Automation Architect",
    exp: "10 years · Workflow design, enterprise integrations, RPA-to-AI migrations",
  },
  {
    initials: "DO",
    name: "David Osei",
    role: "Principal Data Scientist",
    exp: "9 years · Forecasting, NLP, computer vision for quality systems",
  },
  {
    initials: "LK",
    name: "Lena Krüger",
    role: "AI Solutions Lead",
    exp: "8 years · Conversational AI, voice agents, multilingual deployments",
  },
];

export const Team = () => {
  return (
    <section id="team" className="relative py-28 lg:py-36 px-6" data-testid="team-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-16 lg:mb-20 max-w-2xl"
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#00F2FE] mb-5">Chapter 05 — The crew</p>
          <h2 className="font-['Outfit'] font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#E8F1F5]">
            Seasoned minds, <span className="gradient-text font-medium">deep AI experience</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#8AA1B1]">
            A senior-only team of engineers and scientists who have shipped AI into production
            across healthcare, finance, logistics and beyond.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {TEAM.map((m, i) => (
            <motion.div
              key={m.name}
              data-testid={`team-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.1 }}
              whileHover={{ y: -6 }}
              className="group rounded-2xl border border-[#00F2FE]/15 bg-[#051221]/70 backdrop-blur-sm p-8 text-center hover:border-[#00F2FE]/45 hover:shadow-[0_0_35px_-10px_rgba(0,242,254,0.35)] transition-[border-color,box-shadow] duration-500"
            >
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#00F2FE]/40 to-[#10E59A]/40 blur-md group-hover:blur-lg transition-[filter] duration-300" />
                <div className="relative w-full h-full rounded-full border border-[#00F2FE]/40 bg-[#051221] flex items-center justify-center">
                  <span className="font-['Outfit'] font-semibold text-xl gradient-text">{m.initials}</span>
                </div>
              </div>
              <h3 className="font-['Outfit'] font-medium text-lg text-[#E8F1F5]">{m.name}</h3>
              <p className="text-sm text-[#00F2FE] mt-1.5">{m.role}</p>
              <p className="text-xs leading-relaxed text-[#8AA1B1] mt-4">{m.exp}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-14 flex items-center justify-center gap-3 text-[#8AA1B1]/70 text-sm"
        >
          <BrainCircuit className="w-4 h-4 text-[#10E59A]" />
          Backed by a wider network of specialist AI engineers across three continents
        </motion.div>
      </div>
    </section>
  );
};
